import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ContactService, type ContactFormData } from "@/services/contactService";
import { validateContactForm } from '@/utils/enhancedValidation';

interface ContactFormProps {
  variant?: "compact" | "full";
  title?: string;
  description?: string;
}

const ContactForm = ({ variant = "full", title, description }: ContactFormProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    projectType: "",
    workAmount: "",
    location: "",
    startDate: "",
    description: "",
    acceptContact: false,
    acceptData: false
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Client-side validation
      if (!ContactService.isValidEmail(formData.email)) {
        toast({
          title: "Email invalide",
          description: "Veuillez saisir une adresse email valide.",
          variant: "destructive",
        });
        return;
      }

      if (!ContactService.isValidPhone(formData.phone)) {
        toast({
          title: "Téléphone invalide",
          description: "Veuillez saisir un numéro de téléphone français valide.",
          variant: "destructive",
        });
        return;
      }

      // Submit to secure database
      const result = await ContactService.submitContact(formData);
      
      if (result.success) {
        toast({
          title: "Demande envoyée avec succès",
          description: "Nous vous recontacterons dans les plus brefs délais pour discuter de votre projet.",
        });

        // Reset form on success
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          projectType: "",
          workAmount: "",
          location: "",
          startDate: "",
          description: "",
          acceptContact: false,
          acceptData: false
        });
      } else {
        toast({
          title: "Erreur lors de l'envoi",
          description: result.error || "Une erreur s'est produite. Veuillez réessayer.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erreur technique",
        description: "Une erreur inattendue s'est produite. Veuillez réessayer plus tard.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (variant === "compact") {
    return (
      <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm">
        <CardHeader className="text-center pb-4">
          <h2 className="text-xl font-semibold tracking-tight text-foreground">
            {title || "Parlez-nous de votre projet"}
          </h2>
          {description && (
            <p className="text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="firstName" className="text-xs">Prénom *</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  required
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lastName" className="text-xs">Nom *</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  required
                  className="h-9"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="email" className="text-xs">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                required
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="phone" className="text-xs">Téléphone *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                required
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="projectType" id="projectType-label" className="text-xs">Type de projet *</Label>
              <Select 
                value={formData.projectType}
                onValueChange={(value) => handleInputChange("projectType", value)}
              >
                <SelectTrigger className="h-9" aria-labelledby="projectType-label">
                  <SelectValue placeholder="Sélectionnez" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="construction-neuve">Construction neuve</SelectItem>
                  <SelectItem value="renovation-lourde">Rénovation lourde</SelectItem>
                  <SelectItem value="extension">Extension</SelectItem>
                  <SelectItem value="surelevation">Surélévation</SelectItem>
                  <SelectItem value="maison-individuelle">Maison individuelle</SelectItem>
                  <SelectItem value="copropriete">Copropriété</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="acceptContact"
                  checked={formData.acceptContact}
                  onCheckedChange={(checked) => handleInputChange("acceptContact", checked as boolean)}
                  required
                />
                <Label htmlFor="acceptContact" className="text-xs leading-relaxed">
                  J'accepte d'être contacté pour recevoir des informations sur l'assurance DO.
                </Label>
              </div>
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="acceptData"
                  checked={formData.acceptData}
                  onCheckedChange={(checked) => handleInputChange("acceptData", checked as boolean)}
                  required
                />
                <Label htmlFor="acceptData" className="text-xs leading-relaxed">
                  J'accepte le traitement de mes données conformément au RGPD.
                </Label>
              </div>
            </div>

            <Button 
              type="submit" 
              size="sm" 
              className="w-full"
              disabled={!formData.acceptContact || !formData.acceptData || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Envoi...
                </>
              ) : (
                "Être recontacté"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  // Full form variant (existing full form)
  return (
    <Card>
      <CardHeader className="text-center">
        <h2 className="text-2xl md:text-3xl font-semibold leading-none tracking-tight">
          {title || "Recevez plus d'informations sur votre projet"}
        </h2>
        <p className="text-muted-foreground">
          {description || "Remplissez ce formulaire pour être recontacté par un expert"}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informations personnelles */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold border-b pb-2">
              Vos informations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="firstName">Prénom *</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Nom *</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* Informations projet */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold border-b pb-2">
              Votre projet
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="projectType" id="projectType-full-label">Type de projet *</Label>
                <Select 
                  value={formData.projectType}
                  onValueChange={(value) => handleInputChange("projectType", value)}
                >
                  <SelectTrigger aria-labelledby="projectType-full-label">
                    <SelectValue placeholder="Sélectionnez votre projet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="construction-neuve">Construction neuve</SelectItem>
                    <SelectItem value="renovation-lourde">Rénovation lourde</SelectItem>
                    <SelectItem value="extension">Extension</SelectItem>
                    <SelectItem value="surelevation">Surélévation</SelectItem>
                    <SelectItem value="maison-individuelle">Maison individuelle</SelectItem>
                    <SelectItem value="copropriete">Copropriété</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="workAmount" id="workAmount-label">Montant des travaux</Label>
                <Select onValueChange={(value) => handleInputChange("workAmount", value)}>
                  <SelectTrigger aria-labelledby="workAmount-label">
                    <SelectValue placeholder="Sélectionnez le montant" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="moins-50k">Moins de 50 000€</SelectItem>
                    <SelectItem value="50k-100k">50 000€ - 100 000€</SelectItem>
                    <SelectItem value="100k-200k">100 000€ - 200 000€</SelectItem>
                    <SelectItem value="200k-500k">200 000€ - 500 000€</SelectItem>
                    <SelectItem value="plus-500k">Plus de 500 000€</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Localisation du projet</Label>
                <Input
                  id="location"
                  type="text"
                  placeholder="Ville, département..."
                  value={formData.location}
                  onChange={(e) => handleInputChange("location", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="startDate">Date de début prévue</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange("startDate", e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description de votre projet</Label>
              <Textarea
                id="description"
                placeholder="Décrivez votre projet en quelques mots..."
                className="min-h-[120px]"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                Plus votre description est précise, plus nos conseils seront adaptés à vos besoins.
              </p>
            </div>
          </div>

          {/* Consentements */}
          <div className="space-y-4">
            <div className="flex items-start space-x-2">
              <Checkbox
                id="acceptContact"
                checked={formData.acceptContact}
                onCheckedChange={(checked) => handleInputChange("acceptContact", checked as boolean)}
                required
              />
              <Label htmlFor="acceptContact" className="text-sm leading-relaxed">
                J'accepte d'être contacté par téléphone et email pour recevoir des informations sur l'assurance DO. 
                Vous pouvez vous désabonner à tout moment.
              </Label>
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox
                id="acceptData"
                checked={formData.acceptData}
                onCheckedChange={(checked) => handleInputChange("acceptData", checked as boolean)}
                required
              />
              <Label htmlFor="acceptData" className="text-sm leading-relaxed">
                Conformément au RGPD, vos données ne seront utilisées que pour vous proposer des informations personnalisées. 
                Elles ne seront jamais vendues à des tiers.
              </Label>
            </div>
          </div>

          <Button 
            type="submit" 
            size="lg" 
            className="w-full"
            disabled={!formData.acceptContact || !formData.acceptData || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              "Être recontacté par un expert"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;